// Login Page — Dark theme, paleta emerald/teal/cyan
import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import type { FormEvent } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Loading } from '../components/ui/loading';
import { Zap, AlertCircle, Eye, EyeOff, ArrowRight } from 'lucide-react';

// Tiempos de bloqueo en segundos por número de intento (índice = intento - 5)
const LOCKOUT_DURATIONS = [30, 60, 120];

/**
 * Hook para cuenta regresiva que evita setState dentro de useEffect.
 * Usa useSyncExternalStore para leer el tiempo restante de forma reactiva.
 */
function useLockCountdown(lockUntil: number): number {
  const listenersRef = useRef(new Set<() => void>());
  const secondsRef = useRef(0);

  // Calcular segundos restantes
  const getSnapshot = () => {
    if (lockUntil <= 0) return 0;
    return Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
  };

  useEffect(() => {
    if (lockUntil <= 0) return;
    const tick = () => {
      const secs = Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
      if (secs !== secondsRef.current) {
        secondsRef.current = secs;
        listenersRef.current.forEach((cb) => cb());
      }
      if (secs <= 0) clearInterval(timer);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [lockUntil]);

  return useSyncExternalStore(
    (cb) => {
      listenersRef.current.add(cb);
      return () => listenersRef.current.delete(cb);
    },
    getSnapshot,
    getSnapshot,
  );
}

export default function LoginPage() {
  const location = useLocation();
  const { signIn, loading, error, clearError, user, initialized, emailVerified } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // H-F020: Protección contra fuerza bruta — persistir en sessionStorage para sobrevivir recargas
  const [failedAttempts, setFailedAttempts] = useState(() => {
    try {
      return Number(sessionStorage.getItem('rpa_login_attempts')) || 0;
    } catch { return 0; }
  });
  const [lockUntil, setLockUntil] = useState(() => {
    try {
      return Number(sessionStorage.getItem('rpa_login_lockUntil')) || 0;
    } catch { return 0; }
  }); // timestamp Unix en ms
  const lockSecondsLeft = useLockCountdown(lockUntil);

  // H-F020: Persistir failedAttempts y lockUntil en sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('rpa_login_attempts', String(failedAttempts));
      sessionStorage.setItem('rpa_login_lockUntil', String(lockUntil));
    } catch { /* sessionStorage no disponible */ }
  }, [failedAttempts, lockUntil]);

  // M-01: Soporte para redirect after login (returnTo)
  // H-F053: Validar que returnTo sea string antes de usarlo
  // M-06: Validar que returnTo sea ruta relativa (previene open redirect)
  // [H-F001] Rechazar protocol-relative URLs (//evil.com) que startsWith('/') acepta
  const locationState = location.state as Record<string, unknown> | null;
  const rawReturnTo = typeof locationState?.returnTo === 'string' ? locationState.returnTo : undefined;
  const returnTo = (rawReturnTo?.startsWith('/') && !rawReturnTo.startsWith('//')) ? rawReturnTo : '/dashboard';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();

    // H-F001: Verificar si la cuenta está bloqueada por intentos fallidos
    if (lockUntil > Date.now()) {
      return;
    }

    try {
      await signIn(email, password);
      // No navegar aquí — cuando onAuthStateChanged actualice el estado,
      // el componente re-renderiza y <Navigate> redirige automáticamente
      // Resetear contador en login exitoso
      setFailedAttempts(0);
      setLockUntil(0);
      // H-F020: Limpiar sessionStorage en login exitoso
      try {
        sessionStorage.removeItem('rpa_login_attempts');
        sessionStorage.removeItem('rpa_login_lockUntil');
      } catch { /* sessionStorage no disponible */ }
    } catch (err) {
      // H-F001: Incrementar intentos solo en errores de credenciales,
      // no en errores de red (que no son culpa del usuario)
      const errCode = (err as { code?: string })?.code ?? '';
      const isCredentialError = errCode.includes('wrong-password') ||
        errCode.includes('user-not-found') ||
        errCode.includes('invalid-credential') ||
        errCode.includes('invalid-email') ||
        errCode.includes('too-many-requests');

      if (isCredentialError) {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        // Bloquear a partir del 5.º intento fallido
        if (newAttempts >= 5) {
          const durationIndex = Math.min(newAttempts - 5, LOCKOUT_DURATIONS.length - 1);
          const durationSec = LOCKOUT_DURATIONS[durationIndex];
          setLockUntil(Date.now() + durationSec * 1000);
        }
      }
      // Error de credenciales ya está manejado en el store
    }
  };

  // I-02: Mostrar spinner mientras Firebase verifica sesión (evita flash del formulario para usuarios autenticados)
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <Loading size="lg" />
      </div>
    );
  }

  // Usuario ya autenticado → redirigir según estado de verificación
  // [H-T005] Pasar returnTo a /verify-email para no perderlo durante la verificación
  if (user) {
    if (!emailVerified) {
      return <Navigate to="/verify-email" replace state={{ returnTo }} />;
    }
    return <Navigate to={returnTo} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4 relative overflow-hidden">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 mesh-gradient" />
      <div className="absolute inset-0 dot-pattern opacity-30" />
      <div className="absolute top-20 -right-40 w-[400px] h-[400px] rounded-full bg-emerald-500/5 blur-3xl" />
      <div className="absolute -bottom-20 -left-40 w-[300px] h-[300px] rounded-full bg-teal-500/5 blur-3xl" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 p-2 inline-flex shadow-lg shadow-emerald-500/20">
              <Zap className="h-6 w-6 text-white" />
            </span>
            <span className="text-2xl font-extrabold tracking-tight text-white">
              Proceso<span className="text-gradient">Fácil</span>
            </span>
          </Link>
        </div>

        {/* Card de login — glass */}
        <div
          className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 shadow-2xl shadow-black/20"
          style={{ backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
        >
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-1">Iniciar sesión</h1>
            <p className="text-sm text-gray-400">Ingresa a tu cuenta para continuar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* H-F001: Aviso de bloqueo por intentos fallidos */}
            {lockSecondsLeft > 0 && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  Demasiados intentos fallidos. Intenta nuevamente en{' '}
                  <strong>{lockSecondsLeft}s</strong>.
                </span>
              </div>
            )}
            {error && !lockSecondsLeft && (
              <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg text-rose-300 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoFocus
                className="flex h-10 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="flex h-10 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 pr-10 text-sm text-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || lockSecondsLeft > 0}
              className="w-full h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-sm font-semibold shadow-lg shadow-emerald-500/20 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 inline-flex items-center justify-center btn-glow"
            >
              {loading ? (
                <>
                  <Loading size="sm" className="mr-2" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  Iniciar sesión
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>

            <div className="text-center text-sm text-gray-400">
              ¿No tienes una cuenta?{' '}
              {/* NAV-10: Pasar returnTo al registro para que el usuario sea redirigido
                  a la URL original después de registrarse */}
              <Link
                to="/register"
                state={{ returnTo }}
                className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
              >
                Regístrate
              </Link>
            </div>
          </form>
        </div>

        {/* Link de regreso */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
